import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Table,
  theme,
  Tooltip
} from 'antd'
import dayjs from 'dayjs'
import React from 'react'
import { usePatientPayment } from '../hooks'

/**
 * Component for displaying and managing patient payment form
 * @param {Object} data - Initial data for the patient payment
 */
const PatientPaymentForm = ({ data }) => {
  const [form] = Form.useForm()
  const { token } = theme.useToken()

  // Initialize the patient payment hook
  const {
    loading,
    formLoading,
    submitLoading,
    paymentData,
    items,
    renderForm,
    handleGetBillingRecords,
    handleAllocatePayment,
    handleCloseTab,
    handleFieldChange,
    handleTableFieldChange,
    generateTotalsData,
    setItems
  } = usePatientPayment(data)

  return (
    <Row>
      <Col span={24}>
        <Card size='small' title={<span style={{ color: token.colorPrimary, fontSize: 18 }}>New Patient Payment</span>}>
          <Card
            size='small'
            bordered={false}
            title={<span style={{ color: token.colorSecondary, fontSize: 18 }}>Account Details</span>}
            type='inner'
            loading={formLoading}
          >
            {renderForm ? (
              <Form
                form={form}
                initialValues={{
                  ...paymentData,
                  account: paymentData.patientRecord
                    ? `${paymentData.patientRecord.idNo} - ${paymentData.patientRecord.firstName} ${paymentData.patientRecord.lastName}`
                    : '',
                  practiceReceiptNumber: dayjs(new Date()).format('YYYYMMDDHHmmss'),
                  receiptDate: dayjs(new Date())
                }}
                onValuesChange={handleFieldChange}
                layout='vertical'
              >
                <Row gutter={[12, 12]}>
                  <Col span={12}>
                    <Form.Item
                      name='account'
                      label='Account'
                      rules={[{ required: true, message: 'Please select an Account' }]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Row gutter={[12, 12]}>
                      <Col span={12}>
                        <Form.Item
                          name='accountStatus'
                          label='Account Status'
                          rules={[{ required: true, message: 'Please select an Account Status' }]}
                        >
                          <Select
                            options={[
                              { label: 'Normal', value: 'normal' },
                              { label: 'Suspended', value: 'suspended' },
                              { label: 'Hand Over', value: 'handOver' },
                              { label: 'Bad Debt', value: 'badDebt' }
                            ]}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          name='paymentMethod'
                          label='Payment Method'
                          rules={[{ required: true, message: 'Please select a Payment Method' }]}
                        >
                          <Select
                            options={[
                              { label: 'Bank Transfer (EFT)', value: 'eft' },
                              { label: 'Cash', value: 'cash' },
                              { label: 'Credit/Debit Card', value: 'card' },
                              { label: 'Online Credit/Debit Card', value: 'onlineCard' },
                              { label: 'Discount', value: 'discount' }
                            ]}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Col>
                </Row>
                <Row gutter={[12, 12]}>
                  <Col span={12}>
                    <Form.Item name='practiceReceiptNumber' label='Practice Receipt Number'>
                      <Input readOnly />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Row gutter={[12, 12]}>
                      <Col span={12}>
                        <Form.Item
                          name='receiptDate'
                          label='Receipt Date'
                          rules={[{ required: true, message: 'Please select a Receipt Date' }]}
                        >
                          <DatePicker style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          name='paymentAmount'
                          label='Payment Amount'
                          rules={[{ required: true, message: 'Please provide a Payment Amount' }]}
                        >
                          <Input type='number' prefix='R' />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </Form>
            ) : undefined}
          </Card>
          <Card
            size='small'
            bordered={false}
            type='inner'
            title={<span style={{ color: token.colorSecondary, fontSize: 18 }}>Invoice Items</span>}
            extra={
              <Button size='small' danger onClick={() => handleGetBillingRecords()}>
                <Space>Reset Values</Space>
              </Button>
            }
          >
            <Table
              pagination={false}
              size='small'
              loading={loading}
              tableLayout='fixed'
              columns={[
                { title: 'Item Type', dataIndex: 'type', key: 'type', width: '8%' },
                { title: 'Code', dataIndex: 'code', key: 'code', width: '5%' },
                { title: 'Description', dataIndex: 'name', key: 'description', width: '40%' },
                {
                  title: 'Total',
                  dataIndex: 'total',
                  key: 'total',
                  width: '6%',
                  render: (text) => (
                    <Input style={{ backgroundColor: '#ddd', textAlign: 'right' }} value={text} readOnly />
                  )
                },
                {
                  title: 'Medical Aid',
                  dataIndex: 'medicalAid',
                  key: 'medicalAid',
                  width: '6%',
                  render: (text) => (
                    <Input style={{ backgroundColor: '#ddd', textAlign: 'right' }} value={text} readOnly />
                  )
                },
                {
                  title: 'Patient Liable',
                  dataIndex: 'patientBalance',
                  key: 'patient',
                  width: '6%',
                  render: (text) => (
                    <Input style={{ backgroundColor: '#FCA19C', textAlign: 'right' }} value={text} readOnly />
                  )
                },
                {
                  title: 'Payment',
                  dataIndex: 'payment',
                  key: 'payment',
                  width: '6%',
                  render: (text, record, index) => {
                    const paymentValue = form.getFieldValue('paymentAmount')

                    return (
                      <Tooltip open={paymentValue ? false : undefined} title='Please enter a Payment Amount first'>
                        <InputNumber
                          style={{ width: '100%', textAlign: 'right' }}
                          onKeyDown={(e) => {
                            const tmpItems = JSON.parse(JSON.stringify(items))
                            const currentItem = tmpItems[index]

                            switch (e.key.toUpperCase()) {
                              case 'M':
                                e.preventDefault()
                                if (parseFloat(currentItem.patientBalance) > 0) {
                                  currentItem.medicalAid = (
                                    parseFloat(currentItem.medicalAid) + parseFloat(currentItem.patientBalance)
                                  ).toFixed(2)
                                  currentItem.patientBalance = '0'
                                  currentItem.patient = '0'
                                  setItems(tmpItems)
                                }
                                break
                              case 'P':
                                e.preventDefault()
                                if (parseFloat(currentItem.medicalAid) > 0) {
                                  currentItem.patientBalance = (
                                    parseFloat(currentItem.patientBalance) + parseFloat(currentItem.medicalAid)
                                  ).toFixed(2)
                                  currentItem.patient = currentItem.patientBalance
                                  currentItem.medicalAid = '0'
                                  setItems(tmpItems)
                                }
                                break
                              case 'D':
                                e.preventDefault()
                                if (parseFloat(currentItem.patientBalance) > 0) {
                                  currentItem.discount = (
                                    parseFloat(currentItem.discount) + parseFloat(currentItem.patientBalance)
                                  ).toFixed(2)
                                  currentItem.patientBalance = '0'
                                  currentItem.patient = '0'
                                  setItems(tmpItems)
                                }
                                break
                              case 'R':
                                e.preventDefault()
                                currentItem.patientBalance = currentItem.oldPatient
                                currentItem.patient = currentItem.oldPatient
                                currentItem.medicalAid = currentItem.oldMedicalAid
                                currentItem.payment = '0'
                                currentItem.discount = '0'
                                setItems(tmpItems)
                                break
                              case ' ': // Space key
                                e.preventDefault()
                                if (paymentValue && parseFloat(currentItem.patientBalance) > 0) {
                                  if (parseFloat(paymentValue) >= parseFloat(currentItem.patientBalance)) {
                                    currentItem.payment = currentItem.patientBalance
                                    currentItem.patientBalance = '0'
                                    currentItem.patient = '0'
                                    setItems(tmpItems)
                                  }
                                }
                                break
                              default:
                                break
                            }
                          }}
                          value={text}
                          onChange={(value) => {
                            // Always call handleTableFieldChange, regardless of paymentValue
                            handleTableFieldChange('payment', index, value)
                          }}
                        />
                      </Tooltip>
                    )
                  }
                },
                {
                  title: 'Discount',
                  dataIndex: 'discount',
                  key: 'discount',
                  width: '6%',
                  render: (text, record, index) => (
                    <Input
                      style={{ textAlign: 'right' }}
                      value={text}
                      onChange={(e) => handleTableFieldChange('discount', index, e.target.value)}
                    />
                  )
                },
                {
                  title: 'Comments',
                  dataIndex: 'comments',
                  key: 'comments',
                  width: '10%',
                  render: (text, record, index) => (
                    <Input.TextArea
                      rows={1}
                      value={text}
                      onChange={(e) => handleTableFieldChange('comments', index, e.target.value)}
                    />
                  )
                }
              ]}
              dataSource={items}
              footer={() => {
                return (
                  <Table
                    showHeader={false}
                    size='small'
                    pagination={false}
                    columns={[
                      { width: '8%' },
                      { width: '5%' },
                      { width: '40%', render: () => <b style={{ float: 'right' }}>Total</b> },
                      {
                        width: '6%',
                        dataIndex: 'total',
                        render: (text) => (
                          <div style={{ float: 'right' }}>
                            <b>{text}</b>
                          </div>
                        )
                      },
                      {
                        width: '6%',
                        dataIndex: 'medicalAid',
                        render: (text) => (
                          <div style={{ float: 'right' }}>
                            <b>{text}</b>
                          </div>
                        )
                      },
                      {
                        width: '6%',
                        dataIndex: 'patient',
                        render: (text) => (
                          <div style={{ float: 'right' }}>
                            <b>{text}</b>
                          </div>
                        )
                      },
                      {
                        width: '6%',
                        dataIndex: 'payment',
                        render: (text) => (
                          <div style={{ float: 'right' }}>
                            <b>{text}</b>
                          </div>
                        )
                      },
                      {
                        width: '6%',
                        dataIndex: 'discount',
                        render: (text) => (
                          <div style={{ float: 'right' }}>
                            <b>{text}</b>
                          </div>
                        )
                      },
                      { width: '10%' }
                    ]}
                    dataSource={generateTotalsData()}
                  />
                )
              }}
            />
          </Card>
        </Card>
        <Row justify='space-around' style={{ marginTop: 10 }}>
          <Col>P = Move to Patient Liable</Col>
          <Col>M = Move to Medical Aid</Col>
          <Col>D = Discount</Col>
          <Col>R = Reset</Col>
          <Col>Space - Allocate</Col>
        </Row>
        <Row justify='center' style={{ marginTop: 10 }} gutter={[12, 12]}>
          <Col>
            <Button type='primary' onClick={() => handleAllocatePayment(form)} loading={submitLoading}>
              Allocate Payment
            </Button>
          </Col>
          <Col>
            <Button danger onClick={() => handleCloseTab()}>
              Close
            </Button>
          </Col>
        </Row>
      </Col>
    </Row>
  )
}

export default PatientPaymentForm
